import { StandardEvents } from '@shopify/events';

/*
 * Aspilon additions to Horizon's behaviour, found in the Stage 9 QA. Horizon's own JavaScript is unchanged;
 * both parts only listen to what Horizon already does.
 *   1. Instant cart count (SPEC §7).
 *   2. Off-screen carousel slides leave the tab order (WCAG 4.1.2 / Lighthouse aria-hidden-focus).
 *   3. The header's bottom and end edges as CSS variables, for the search and account popovers' position.
 *   4. Swipe down to close the compact filter bottom sheet (Components-M3).
 *   5. The mega menu's how-to video pauses when its panel closes (SPEC §7: nothing moves without a user action).
 */

/**
 * 1. The cart count updates the moment something is added (SPEC §7, Stage 9 timing check).
 *
 * Horizon's cart icon renders the count only when Shopify has answered the add request (about 1 s
 * through the theme preview). This listens to the same CartLinesUpdateEvent Horizon's product form,
 * quick add and cart add-on send, and shows the new count at once through the cart icon's own
 * renderCartBubble(). Horizon's JavaScript is unchanged: when the answer arrives, Horizon renders the
 * real count over this one, so a failed add corrects itself; if the request fails outright, the count
 * is read back from the cart.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add') return;

  const added = (event.lines ?? []).reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
  if (added <= 0) return;

  const icons = [...document.querySelectorAll('cart-icon')];
  for (const icon of icons) {
    if (typeof icon.renderCartBubble !== 'function') continue;
    icon.renderCartBubble((icon.currentCartCount || 0) + added, false);
  }

  event.promise?.catch(async () => {
    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      for (const icon of icons) icon.renderCartBubble?.(cart.item_count ?? 0, false);
    } catch {
      // Horizon's own pageshow check corrects the count on the next page.
    }
  });
});

/**
 * 2. Horizon's slideshow marks off-screen slides aria-hidden="true", but their links and buttons stay focusable,
 * so keyboard focus can land on content screen readers are told to ignore. Keep `inert` in step with the
 * aria-hidden Horizon sets, on every slide now and later.
 */
const syncSlide = (slide) => {
  slide.inert = slide.getAttribute('aria-hidden') === 'true';
};

const slideObserver = new MutationObserver((records) => {
  for (const record of records) {
    if (record.type === 'attributes' && record.target instanceof Element && record.target.matches('slideshow-slide')) {
      syncSlide(record.target);
    }
    for (const node of record.addedNodes ?? []) {
      if (node instanceof Element) node.querySelectorAll?.('slideshow-slide[aria-hidden]').forEach(syncSlide);
    }
  }
});

document.querySelectorAll('slideshow-slide[aria-hidden]').forEach(syncSlide);
slideObserver.observe(document.documentElement, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ['aria-hidden'],
});

/**
 * 3. The search popover is a fixed dialog that Horizon centres in the window, so it can overlap the header;
 * the account popover is pinned 20px from the window's edge, past the header's end edge on wide screens.
 * The header is sticky and moves up when the promo bar scrolls away, which CSS can't follow. This keeps
 * --asp-header-bottom (px from the top of the window) current; aspilon-overrides.css places the dialog
 * that far down plus --asp-comp-popover-offset, the same gap as the locale popover; --asp-header-end-gap
 * (px from the window's end edge to the header's) aligns the account popover with the header.
 */
let headerFrame = 0;
const setHeaderBottom = () => {
  headerFrame = 0;
  // Looked up each time: the theme editor re-renders the header, which leaves an earlier reference detached.
  const header = document.querySelector('#header-component');
  if (!header) return;
  const rect = header.getBoundingClientRect();
  const root = document.documentElement;
  const endGap = document.dir === 'rtl' || root.dir === 'rtl' ? rect.left : root.clientWidth - rect.right;
  root.style.setProperty('--asp-header-bottom', `${Math.round(rect.bottom)}px`);
  // The account popover (Shopify's <shopify-account>) lines up with the header's end edge.
  root.style.setProperty('--asp-header-end-gap', `${Math.max(0, Math.round(endGap))}px`);
};
const queueHeaderBottom = () => {
  if (!headerFrame) headerFrame = requestAnimationFrame(setHeaderBottom);
};

setHeaderBottom();
// Horizon scrolls .page-wrapper rather than the document; scroll events don't bubble, so listen in the capture phase.
document.addEventListener('scroll', queueHeaderBottom, { capture: true, passive: true });
window.addEventListener('resize', queueHeaderBottom, { passive: true });
document.addEventListener('shopify:section:load', queueHeaderBottom);

/**
 * 4. On compact, Horizon's filter drawer is restyled as a bottom sheet (aspilon-overrides.css). M3 bottom sheets
 * also close with a downward swipe. A swipe that starts on the sheet's top area (handle and header) and moves
 * down far enough calls the drawer's own close(), the same one its close button and Esc use. The sheet doesn't
 * follow the finger; it closes with its normal animation.
 */
const SHEET_SWIPE_CLOSE = 64;
const compact = window.matchMedia('(max-width: 749px)');
let sheetSwipe = null;

document.addEventListener(
  'pointerdown',
  (event) => {
    if (!compact.matches || event.pointerType === 'mouse') return;
    const dialog = event.target instanceof Element ? event.target.closest('#filters-drawer .theme-drawer__dialog[open]') : null;
    if (!dialog) return;
    const header = dialog.querySelector('.theme-drawer__header');
    const top = dialog.getBoundingClientRect().top;
    const limit = header ? header.getBoundingClientRect().bottom : top + 80;
    if (event.clientY > limit) return;
    sheetSwipe = { id: event.pointerId, y: event.clientY };
  },
  { passive: true }
);

document.addEventListener(
  'pointerup',
  (event) => {
    if (!sheetSwipe || event.pointerId !== sheetSwipe.id) return;
    const distance = event.clientY - sheetSwipe.y;
    sheetSwipe = null;
    if (distance < SHEET_SWIPE_CLOSE) return;
    const drawer = document.getElementById('filters-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  },
  { passive: true }
);

document.addEventListener('pointercancel', () => {
  sheetSwipe = null;
});

/**
 * 5. The Aspilon mega menu panel (snippets/aspilon-mega-menu.liquid) sits in Horizon's submenu, which Horizon
 * closes by setting `inert`. A video the visitor started keeps playing, muted and out of sight, once the panel
 * closes; pause it then. Horizon's menu code is unchanged: this only watches the attribute it sets.
 */
const pauseClosedMegaVideos = (submenu) => {
  if (!submenu.hasAttribute('inert')) return;
  submenu.querySelectorAll('.aspilon-mega video').forEach((video) => {
    if (!video.paused) video.pause();
  });
};

const megaObserver = new MutationObserver((records) => {
  for (const record of records) {
    if (record.target instanceof Element) pauseClosedMegaVideos(record.target);
  }
});

// One observer on the document, so a submenu that is re-rendered (theme editor) is still watched.
megaObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['inert'], subtree: true });
